// Agents › Rooms (`/c/{slug}/agents/rooms`; M4-06, Agents UX pass 2026-10-21; spec 06 §Channels
// "Agent rooms", research recs 11–12): every agent room the caller can see, one 36 px row each:
// the room's name (a link into Chat), its participants with their badges, whose turn it is (or
// that it is paused after too many agent turns, or taken over by whom), parked runs and open
// status asks, and the hover actions: Open in Chat, Join / Leave, Take over / Release. One call
// on load (`agentRooms.list`); a take-over goes through the channel (or topic) it lives in, as
// the Chat header does. Without the add-on: the banner alone (no call, no sample rows).

import type { OperationOutput } from "@companyos/sdk";
import {
  AvatarStack,
  Badge,
  buttonVariants,
  cn,
  EmptyState,
  ListRow,
  LocationHeader,
  SearchField,
  Toolbar,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  HandIcon,
  LogInIcon,
  LogOutIcon,
  MessageSquareIcon,
  PlayIcon,
  UsersRoundIcon,
} from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { useFeature } from "../use-feature";
import { formatElapsed } from "./agent-parts";
import {
  AgentsOffBanner,
  LIST_BODY,
  RowAction,
  RowLinkAction,
  useMyMembershipId,
} from "./agents-ux";

export type RoomView = OperationOutput<"agentRooms.get">;

export const roomKeys = {
  list: (co: string) => ["company", co, "agent-rooms"] as const,
};

export const roomsQuery = (api: Api, co: string) => ({
  queryKey: roomKeys.list(co),
  initialPageParam: undefined as string | undefined,
  queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
    api.client.agentRooms.list({
      params: { co },
      query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
    }),
  getNextPageParam: (last: { next_cursor: string | null }) => last.next_cursor ?? undefined,
});

/** "Riley's turn · 2 agent turns" / "Paused after 8 agent turns" / "Taken over by Maya". */
export function turnLine(room: RoomView, now = Date.now()): string {
  if (room.taken_over_by_membership_id) {
    const who = room.participants.find((p) => p.membership_id === room.taken_over_by_membership_id);
    return `Taken over by ${who?.display_name ?? "a person"}${
      room.taken_over_at ? ` · ${formatElapsed(room.taken_over_at, null, now)}` : ""
    }`;
  }
  const r = room.room;
  if (r.stalled_at || r.agent_turns_since_human >= r.max_agent_turns)
    return `Paused after ${r.agent_turns_since_human} agent turns · post to continue`;
  const turn = room.participants.find((p) => p.is_turn);
  if (!turn) return "No turn yet";
  const since = r.turn_started_at ? ` · ${formatElapsed(r.turn_started_at, null, now)}` : "";
  return `${workerName(turn.display_name)}'s turn${since} · ${r.agent_turns_since_human} agent turn${
    r.agent_turns_since_human === 1 ? "" : "s"
  } since a person`;
}

export function RoomsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const agents = useFeature("agents");
  const q = useInfiniteQuery({ ...roomsQuery(api, company.id), enabled: agents.enabled });
  const [search, setSearch] = useState("");
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  const term = search.trim().toLowerCase();
  const shown = term ? rows.filter((r) => r.name.toLowerCase().includes(term)) : rows;
  return (
    <div className={LIST_BODY} data-slot="rooms">
      <LocationHeader
        icon={UsersRoundIcon}
        title="Rooms"
        {...(q.isSuccess ? { count: rows.length } : {})}
      />
      {agents.enabled ? null : <AgentsOffBanner company={company} />}
      {!agents.enabled ? null : (
        <Toolbar bleed aria-label="Rooms tools">
          <SearchField
            value={search}
            placeholder="Search rooms…"
            aria-label="Search rooms"
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            A room is a channel or topic where agents take turns; people join and can take over.
          </span>
        </Toolbar>
      )}
      {!agents.enabled ? null : q.isPending ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading rooms" rows={2} />
        </div>
      ) : q.isError ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="Rooms" error={q.error} />
        </div>
      ) : !rows.length ? (
        <EmptyState
          variant="compact"
          icon={UsersRoundIcon}
          title="No agent rooms yet"
          description="Make one from a channel's header in Chat (Agent room › Set up turns)."
          actions={
            <Link
              to="/c/$slug/chat"
              params={{ slug: company.slug }}
              search={keepFocus}
              className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
              data-slot="rooms-open-chat"
            >
              <MessageSquareIcon aria-hidden="true" />
              Open Chat
            </Link>
          }
          className="m-6"
        />
      ) : (
        <>
          <div data-slot="rooms-list">
            {shown.map((r) => (
              <RoomRow key={r.room.id} api={api} company={company} room={r} />
            ))}
          </div>
          <LoadMore
            what="rooms"
            hasMore={q.hasNextPage}
            loading={q.isFetchingNextPage}
            onMore={() => void q.fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}

function RoomRow({ api, company, room }: { api: Api; company: CompanyAccess; room: RoomView }) {
  const qc = useQueryClient();
  const co = company.id;
  const refresh = () => void qc.invalidateQueries({ queryKey: roomKeys.list(co) });
  const fail = (fallback: string) => (e: unknown) =>
    toast.add({ title: problemText(e, fallback), type: "error" });
  const takeOver = useMutation({
    mutationFn: async () => {
      if (room.room.topic_id)
        await api.client.topics.takeOver({ params: { co, id: room.room.topic_id } });
      else await api.client.channels.takeOver({ params: { co, id: room.room.channel_id } });
    },
    onSuccess: () => {
      refresh();
      toast.add({ title: `You took over ${room.name}.`, type: "success" });
    },
    onError: fail("The room could not be taken over."),
  });
  const release = useMutation({
    mutationFn: async () => {
      if (room.room.topic_id)
        await api.client.topics.release({ params: { co, id: room.room.topic_id } });
      else await api.client.channels.release({ params: { co, id: room.room.channel_id } });
    },
    onSuccess: () => {
      refresh();
      toast.add({ title: `${room.name} released; the agents continue.`, type: "success" });
    },
    onError: fail("The room could not be released."),
  });
  const join = useMutation({
    mutationFn: (on: boolean) =>
      on
        ? api.client.agentRooms.join({ params: { co, id: room.room.id } })
        : api.client.agentRooms.leave({ params: { co, id: room.room.id } }),
    onSuccess: (_r, on) => {
      refresh();
      toast.add({ title: on ? "You joined the room." : "You left the room.", type: "success" });
    },
    onError: fail("The room could not be changed."),
  });
  const agentsIn = room.participants.filter((p) => p.kind === "agent");
  const people = room.participants.filter((p) => p.kind !== "agent");
  const me = useMyMembershipId(company.id);
  const inRoom = me !== null && room.participants.some((p) => p.membership_id === me);
  const takenOver = room.taken_over_by_membership_id !== null;
  const chatParams = room.room.topic_id
    ? { slug: company.slug, channelId: room.room.channel_id, topicId: room.room.topic_id }
    : { slug: company.slug, channelId: room.room.channel_id };
  const chatTo = room.room.topic_id
    ? "/c/$slug/chat/$channelId/$topicId"
    : "/c/$slug/chat/$channelId";
  return (
    <ListRow
      data-row
      data-room={room.room.id}
      data-taken-over={takenOver || undefined}
      lead={<UsersRoundIcon aria-hidden="true" />}
      title={room.name}
      render={<Link to={chatTo} params={chatParams} search={keepFocus} />}
      chips={
        <>
          {takenOver ? (
            <Badge variant="danger" dot>
              Taken over
            </Badge>
          ) : null}
          {room.parked_runs ? <Badge variant="warning">{room.parked_runs} parked</Badge> : null}
          {room.open_status_asks ? (
            <Badge variant="info">{room.open_status_asks} status asks</Badge>
          ) : null}
        </>
      }
      meta={<span data-slot="turn-line">{turnLine(room)}</span>}
      trailing={
        <span className="flex items-center gap-2" data-slot="participants">
          <AvatarStack
            items={agentsIn.map((p) => ({
              name: workerName(p.display_name),
              kind: "agent" as const,
            }))}
          />
          <span className="flex items-center gap-1 max-md:hidden">
            {agentsIn.map((p, i) => (
              <span key={p.membership_id} className="inline-flex items-center gap-1">
                <WorkerBadge kind={p.badge.kind === "de" ? "de" : "ag"} size="sm" />
                <span>
                  {workerName(p.display_name)}
                  {i < agentsIn.length - 1 ? "," : ""}
                </span>
              </span>
            ))}
            {people.length
              ? ` · ${people.length} ${people.length === 1 ? "person" : "people"}`
              : ""}
          </span>
        </span>
      }
      actions={
        <>
          <RowLinkAction
            icon={MessageSquareIcon}
            label="Open in Chat"
            to={chatTo}
            params={chatParams}
          />
          {inRoom ? (
            <RowAction
              icon={LogOutIcon}
              label="Leave the room"
              disabled={join.isPending}
              onClick={() => join.mutate(false)}
            />
          ) : (
            <RowAction
              icon={LogInIcon}
              label="Join the room"
              disabled={join.isPending}
              onClick={() => join.mutate(true)}
            />
          )}
          {takenOver ? (
            <RowAction
              icon={PlayIcon}
              label="Release the room"
              disabled={release.isPending}
              onClick={() => release.mutate()}
            />
          ) : (
            <RowAction
              icon={HandIcon}
              label="Take over the room"
              disabled={takeOver.isPending}
              onClick={() => takeOver.mutate()}
            />
          )}
        </>
      }
    />
  );
}
